/**
 * A linha do tempo crua de uma implantação — a fonte de onde a esteira é derivada.
 *
 * **Por que mostrar o cru ao lado do derivado.** A esteira responde "em que etapa estamos"; ela
 * é uma leitura, e leitura não serve para conferir a si mesma. Quando o console e o cliente
 * discordam sobre uma etapa, a pergunta que resolve é *quais eventos existem, em que ordem, e
 * quem os registrou* — e até a F7 essa pergunta só tinha resposta por `curl`. É também a única
 * tela onde a liberação do ambiente aparece com nome, data e autor, que é o que uma discussão de
 * prazo contratual precisa.
 *
 * **A ordem é a do servidor.** Ele devolve do mais recente para o mais antigo, com desempate por
 * `registrado_em`. Reordenar aqui produziria, em dois eventos do mesmo instante, uma ordem
 * diferente da que a auditoria mostra — duas versões do mesmo minuto, que é justamente o tipo de
 * divergência que esta tela existe para resolver.
 *
 * **Nada aqui é editável, e não é omissão.** Não existe rota que altere ou apague um evento: a
 * esteira só cresce. Uma tela que oferecesse edição prometeria o que o modelo recusa.
 */

import { useState } from "react";
import { classes } from "../../nucleo/classes";
import { rotuloDoAmbiente, rotuloDoEvento, type EventoDaImplantacao } from "../../nucleo/console";
import {
  CATEGORIAS_DA_LINHA_DO_TEMPO,
  categoriaDoEvento,
  plural,
} from "../../nucleo/secoesDaImplantacao";
/* O formatador de instante mora no cofre desde a F6. Uma segunda cópia aqui daria duas grafias
   de data e hora na mesma tela — e a hora fixada em São Paulo é o que faz consultor e cliente
   lerem o mesmo minuto, não o minuto do navegador de cada um. */
import { formatarInstante } from "../../nucleo/cofre";

export interface PropsDaLinhaDoTempo {
  eventos: readonly EventoDaImplantacao[];
  /* O filtro por tipo (Tudo, Esteira, Kickoff, Reuniões, Acessos, Repasse) da tela da
     implantação. Esconde o resto sem mudar a ordem do servidor. */
  comFiltro?: boolean;
}

/* Os campos de detalhe que já apareceram em evento até aqui. Chave desconhecida continua sendo
   mostrada com o próprio nome: o detalhe é vocabulário aberto por rota, e esconder o que esta
   camada não conhece transformaria dado novo em silêncio. */
const ROTULO_DO_DETALHE: Record<string, string> = {
  descricao: "Descrição",
  email: "E-mail",
  convite: "Convite",
  papel: "Perfil de acesso",
  observacao: "Observação",
  agendada_para_anterior: "Data e hora anterior",
  agendada_para: "Data e hora",
  remarcada_por: "Remarcada por",
  remarcada: "Remarcação",
  duracao_min: "Duração em minutos",
  trilha: "Trilha",
  pergunta_id: "Pergunta",
  referencia: "Referência",
  motivo: "Motivo",
  assunto: "Assunto",
  liberado_em: "Liberado em",
  entrega_prevista: "Entrega prevista",
  trilhas_pendentes: "Trilhas pendentes",
  trilhas_enviadas: "Trilhas enviadas",
  contradicoes: "Divergências no momento do aceite",
  trilhas: "Trilhas",
  modulos: "Módulos",
  modulos_sem_trilha: "Módulos sem perguntas",
  /* Os campos da abertura da implantação, que até a SUS-15400 apareciam com o nome técnico. */
  prazo_dias: "Prazo",
  ambiente_tipo: "Ambiente",
  recursos_plus: "Recursos Plus",
  consultor_da_conta: "Consultor da conta",
};

/* Os valores que chegam como chave de trilha, de módulo ou de assunto. Spec 4.4: a pessoa lê
   "CRM 360", não `crm_360`. Valor desconhecido passa como veio, pelo mesmo motivo do rótulo. */
const TEXTO_DO_VALOR: Record<string, string> = {
  base: "Cadastro base",
  nao_enviado: "Dispensado: o usuário já tem acesso",
  ja_tem_acesso: "Usuário já tem acesso",
  infraestrutura: "Infraestrutura e ambiente",
  analytics: "Analytics",
  crm_360: "CRM 360",
  forca_de_vendas: "Força de Vendas",
  b2b: "B2B / Portal do Lojista",
  estoque: "Estoque",
  fvt: "Força de Vendas Têxtil (FVT)",
  guiarep: "Guia do Representante",
  catalogo_digital: "Catálogo Digital",
  trilha_de_kickoff_enviada: "Trilha do kickoff enviada",
  reuniao_cancelada: "Reunião cancelada",
};

function textoDoValor(valor: unknown): string | null {
  if (valor === null || valor === undefined) return null;
  if (Array.isArray(valor)) {
    const itens = valor.map(textoDoValor).filter((item): item is string => item !== null);
    return itens.length > 0 ? itens.join(", ") : null;
  }
  if (typeof valor === "boolean") return valor ? "sim" : "não";
  const texto = String(valor).trim();
  return texto.length > 0 ? (TEXTO_DO_VALOR[texto] ?? texto) : null;
}

export function LinhaDoTempo({ eventos, comFiltro = false }: PropsDaLinhaDoTempo) {
  const [filtro, setFiltro] = useState<(typeof CATEGORIAS_DA_LINHA_DO_TEMPO)[number]>("Tudo");
  if (eventos.length === 0) {
    return (
      <p className="cartao__texto" data-testid="linha-do-tempo-vazia">
        Nenhum evento registrado nesta implantação ainda.
      </p>
    );
  }

  const visiveis =
    filtro === "Tudo" ? eventos : eventos.filter((evento) => categoriaDoEvento(evento) === filtro);

  return (
    <>
    {comFiltro ? (
      <div className="linha-do-tempo__filtros" role="group" aria-label="Filtrar a linha do tempo por tipo">
        {CATEGORIAS_DA_LINHA_DO_TEMPO.map((categoria) => (
          <button
            key={categoria}
            type="button"
            className={classes("botao botao--pequeno", filtro === categoria ? "" : "botao--fantasma")}
            aria-pressed={filtro === categoria}
            data-testid={`filtro-${categoria}`}
            onClick={() => setFiltro(categoria)}
          >
            {categoria}
          </button>
        ))}
      </div>
    ) : null}
    {/* Quem usa leitor de tela não vê a lista encolher: a contagem é dita a cada troca de filtro. */}
    {comFiltro ? (
      <p className="sr-only" role="status" data-testid="resultado-do-filtro">
        {`${plural(visiveis.length, "evento", "eventos")}${filtro === "Tudo" ? "" : ` de ${filtro}`}.`}
      </p>
    ) : null}
    {/* O aviso já é dito pela contagem acima; aqui ele é só para os olhos, e não é lido duas vezes. */}
    {visiveis.length === 0 ? (
      <p className="cartao__texto" aria-hidden={comFiltro || undefined} data-testid="linha-do-tempo-sem-eventos-do-tipo">
        Nenhum evento deste tipo.
      </p>
    ) : (
    <ol className="linha-do-tempo" data-testid="linha-do-tempo">
      {visiveis.map((evento) => (
        <li
          key={evento.id}
          className="linha-do-tempo__item"
          data-testid={`evento-${evento.tipo}`}
          data-tipo={evento.tipo}
          data-categoria={categoriaDoEvento(evento)}
        >
          <div className="linha-do-tempo__marca" aria-hidden="true" />
          <div className="linha-do-tempo__corpo">
            <h4 className="linha-do-tempo__titulo">{rotuloDoEvento(evento.tipo, evento.detalhe)}</h4>
            <p className="linha-do-tempo__quando numero-tabular">
              {formatarInstante(evento.ocorrido_em) ?? evento.ocorrido_em}
              {evento.ator ? <span className="linha-do-tempo__ator"> · {evento.ator}</span> : null}
            </p>
            <DetalheDoEvento detalhe={evento.detalhe} omitirPergunta={evento.pendencia != null} />
            {evento.pendencia ? (
              <dl className="linha-do-tempo__detalhe" data-testid="registro-da-pendencia">
                {evento.pendencia.pergunta ? (
                  <div>
                    <dt>Pergunta</dt>
                    <dd>{evento.pendencia.pergunta}</dd>
                  </div>
                ) : null}
                <div>
                  <dt>Resposta do cliente</dt>
                  <dd>
                    {evento.pendencia.respondida_em
                      ? formatarInstante(evento.pendencia.respondida_em)
                      : "Ainda não respondida"}
                  </dd>
                </div>
              </dl>
            ) : null}
          </div>
        </li>
      ))}
    </ol>
    )}
    </>
  );
}

function textoDoDetalhe(chave: string, valor: unknown): string | null {
  if ((chave === "agendada_para" || chave === "agendada_para_anterior") && typeof valor === "string")
    return formatarInstante(valor);
  if (chave === "prazo_dias" && typeof valor === "number") return `${valor} ${valor === 1 ? "dia" : "dias"}`;
  if (chave === "ambiente_tipo" && typeof valor === "string") return rotuloDoAmbiente(valor);
  return textoDoValor(valor);
}

function DetalheDoEvento({
  detalhe,
  omitirPergunta = false,
}: {
  detalhe: Record<string, unknown>;
  omitirPergunta?: boolean;
}) {
  const linhas = Object.entries(detalhe)
    .filter(
      ([chave]) =>
        !omitirPergunta || (chave !== "pergunta_id" && chave !== "pergunta_enunciado"),
    )
    .map(([chave, valor]) => [chave, textoDoDetalhe(chave, valor)] as const)
    .filter((par): par is readonly [string, string] => par[1] !== null);

  if (linhas.length === 0) return null;

  return (
    <dl className="linha-do-tempo__detalhe">
      {linhas.map(([chave, valor]) => (
        <div key={chave}>
          <dt>{chave === "agendada_para" && detalhe.remarcada ? "Nova data e hora" : ROTULO_DO_DETALHE[chave] ?? chave}</dt>
          <dd>{valor}</dd>
        </div>
      ))}
    </dl>
  );
}
